import { useTranslate } from "@tolgee/react";
import { useAtom, useAtomValue } from "jotai";
import { ArrowLeft, Loader2, Mail, Ticket } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
	Dialog,
	DialogCloseButton,
	DialogContent,
	DialogDescription,
	DialogTitle,
} from "#/components/ui/dialog";
import { analytics } from "#/lib/analytics";
import { ApiError } from "#/lib/api/api";
import { sendEmailCode, verifyEmailCode } from "#/lib/api/auth";
import {
	clearPendingReferralCode,
	getPendingReferralCode,
	setStoredAuth,
} from "#/lib/api/token-storage";
import {
	loginDialogOpenAtom,
	loginEntryPointAtom,
} from "#/stores/login-dialog-store";

type Step = "email" | "code";

/**
 * Email-code login against the self-hosted backend:
 * `POST /v1/auth/email/send-code` → `POST /v1/auth/email/verify`. A successful
 * verify IS the session (and, for new accounts, creates the server-custodied
 * wallet) — no separate wallet handshake. Opened globally via
 * `login-dialog-store`; mounted once in `AppLayout`.
 */
export function LoginDialog() {
	const { t } = useTranslate();
	const [open, setOpen] = useAtom(loginDialogOpenAtom);
	const entryPoint = useAtomValue(loginEntryPointAtom);
	const [step, setStep] = useState<Step>("email");
	const [email, setEmail] = useState("");
	const [code, setCode] = useState("");
	const [isSendingCode, setIsSendingCode] = useState(false);
	const [isSubmittingCode, setIsSubmittingCode] = useState(false);
	const prevOpenRef = useRef(false);

	const isBusy = isSendingCode || isSubmittingCode;

	useEffect(() => {
		if (open) return;
		setStep("email");
		setEmail("");
		setCode("");
	}, [open]);

	// Funnel: fire when the dialog transitions closed→open. It's opened via a
	// global atom from several call sites; each opener sets `loginEntryPointAtom`
	// right before opening, so the open edge here reads the triggering surface.
	useEffect(() => {
		if (open && !prevOpenRef.current) {
			analytics.track("login_started", { entry_point: entryPoint });
		}
		prevOpenRef.current = open;
	}, [open, entryPoint]);

	// /r/:code에서 저장된 대기 코드 — 로그인 성공 시 여기서 clear한다.
	const pendingReferralCode = useMemo(
		() => (open ? getPendingReferralCode() : null),
		[open],
	);

	const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

	function handleSendCode(event: React.FormEvent) {
		event.preventDefault();
		if (!isValidEmail || isBusy) return;
		setIsSendingCode(true);
		sendEmailCode(email.trim())
			.then(() => {
				analytics.track("login_email_submitted", {});
				setStep("code");
			})
			.catch((err) => {
				analytics.track("login_email_failed", {
					error: err instanceof Error ? err.message : String(err),
				});
				toast.error(
					err instanceof ApiError && err.status === 429
						? t("login.error-too-many-requests")
						: t("login.error-send-code"),
				);
			})
			.finally(() => setIsSendingCode(false));
	}

	function handleSubmitCode(event: React.FormEvent) {
		event.preventDefault();
		if (code.length < 6 || isBusy) return;
		setIsSubmittingCode(true);
		verifyEmailCode({
			email: email.trim(),
			code,
			referral_code: pendingReferralCode,
		})
			.then((response) => {
				// Attribution is set-once on first verify — always clear the pending
				// code after a successful login so we don't retry it.
				clearPendingReferralCode();
				setStoredAuth({
					access_token: response.access_token,
					expires_at: response.expires_at,
					user_id: response.user_id,
					wallet_address: response.wallet_address,
					email: response.email,
					referred_by_code: response.referred_by_code,
					is_new_account: response.is_new_account,
				});
				analytics.track("login_verified", { had_wallet: true });
				setOpen(false);
			})
			.catch((err) => {
				analytics.track("login_code_failed", {
					error: err instanceof Error ? err.message : String(err),
				});
				toast.error(
					err instanceof ApiError && err.status === 429
						? t("login.error-too-many-attempts")
						: t("login.error-invalid-code"),
				);
				setCode("");
			})
			.finally(() => setIsSubmittingCode(false));
	}

	function handleBack() {
		if (isBusy) return;
		setStep("email");
		setEmail("");
		setCode("");
	}

	function handleOpenChange(nextOpen: boolean) {
		if (isBusy) return;
		setOpen(nextOpen);
	}

	return (
		<Dialog open={open} onOpenChange={handleOpenChange}>
			<DialogContent className="w-[min(420px,calc(100vw-1rem))] rounded-[22px] border border-trade-border-subtle bg-[linear-gradient(180deg,var(--trade-surface-overlay),var(--trade-surface-overlay-end))] p-0 shadow-[inset_0_0_0_1px_rgba(255,255,255,0.03),0_36px_72px_rgba(0,0,0,0.52)]">
				<div className="flex items-start justify-between gap-4 px-5 pt-5">
					<div className="min-w-0">
						<DialogTitle className="text-title1 text-trade-text-strong">
							{step === "email" ? t("login.title") : t("login.code-title")}
						</DialogTitle>
						<DialogDescription className="mt-1 text-label2-medium text-trade-text-secondary">
							{step === "email"
								? t("login.subtitle")
								: t("login.code-subtitle", { email: email.trim() })}
						</DialogDescription>
					</div>
					<DialogCloseButton disabled={isBusy} />
				</div>

				<div className="px-5 pb-5 pt-4">
					{step === "email" ? (
						<form onSubmit={handleSendCode} className="flex flex-col gap-3">
							<label className="flex items-center gap-2 rounded-[14px] border border-trade-border-subtle bg-trade-surface-inset px-3.5 py-3 focus-within:border-primary/40">
								<Mail className="h-4 w-4 shrink-0 text-trade-text-faint" />
								<input
									type="email"
									autoFocus
									autoComplete="email"
									value={email}
									onChange={(event) => setEmail(event.target.value)}
									placeholder={t("login.email-placeholder")}
									disabled={isBusy}
									className="h-8 w-full min-w-0 border-none bg-transparent text-body2-medium text-trade-text-strong outline-none placeholder:text-trade-text-faint disabled:opacity-50"
									aria-label={t("login.email-label")}
								/>
							</label>

							{pendingReferralCode ? (
								<div className="flex items-center gap-2 rounded-[14px] border border-trade-border-subtle bg-trade-surface-inset px-3.5 py-2.5">
									<Ticket className="h-4 w-4 shrink-0 text-primary" />
									<span className="text-[12px] font-medium text-trade-text-strong">
										{t("login.referral-applied", {
											code: pendingReferralCode,
										})}
									</span>
								</div>
							) : null}

							<button
								type="submit"
								disabled={!isValidEmail || isBusy}
								className="mt-1 flex min-h-12 w-full items-center justify-center gap-2 rounded-[14px] bg-primary text-body2-bold text-primary-foreground transition-[filter,opacity] hover:brightness-105 disabled:cursor-not-allowed disabled:opacity-45 cursor-pointer"
							>
								{isSendingCode ? (
									<Loader2 className="h-4 w-4 animate-spin" />
								) : (
									t("login.send-code")
								)}
							</button>
						</form>
					) : (
						<form onSubmit={handleSubmitCode} className="flex flex-col gap-3">
							<label className="rounded-[14px] border border-trade-border-subtle bg-trade-surface-inset px-3.5 py-3 focus-within:border-primary/40">
								<input
									type="text"
									inputMode="numeric"
									autoFocus
									autoComplete="one-time-code"
									value={code}
									onChange={(event) =>
										setCode(event.target.value.replace(/\D/g, "").slice(0, 6))
									}
									placeholder="······"
									disabled={isBusy}
									maxLength={6}
									className="h-10 w-full min-w-0 border-none bg-transparent text-center text-title1 text-trade-text-strong outline-none placeholder:text-trade-text-faint disabled:opacity-50"
									aria-label={t("login.code-label")}
								/>
							</label>

							<button
								type="submit"
								disabled={code.length < 6 || isBusy}
								className="mt-1 flex min-h-12 w-full items-center justify-center gap-2 rounded-[14px] bg-primary text-body2-bold text-primary-foreground transition-[filter,opacity] hover:brightness-105 disabled:cursor-not-allowed disabled:opacity-45 cursor-pointer"
							>
								{isSubmittingCode ? (
									<Loader2 className="h-4 w-4 animate-spin" />
								) : (
									t("login.verify")
								)}
							</button>

							<button
								type="button"
								onClick={handleBack}
								disabled={isBusy}
								className="inline-flex items-center justify-center gap-1.5 self-center text-label2-medium text-trade-text-secondary transition-colors hover:text-trade-text-strong disabled:opacity-50 cursor-pointer"
							>
								<ArrowLeft className="h-3 w-3" />
								{t("login.use-different-email")}
							</button>
						</form>
					)}
				</div>
			</DialogContent>
		</Dialog>
	);
}
